/*
 *  -----------------------------------------------------------------------
 *    <copyright file="studies/index.tsx" company="Triumph Research Intelligence">
 *        Copyright (C) 2019 by Triumph Research Intelligence. All rights reserved.
 *    </copyright>
 *   -----------------------------------------------------------------------
 *
 */

import React, { useEffect, useState } from 'react';
import { useLazyQuery, useMutation, useQuery } from '@apollo/client/react';
import NavigationService from '@components-clean/page-nav/navigation.service';
import { orderBy } from '@progress/kendo-data-query';
import ImportStudyConfig from '@components/admin/import-study-config';
import { useAuthorization, useBoolean, useGridCells, useTranslation } from 'hooks';
import {
    OrganisationProductFields,
    OrganisationProgramFields,
    ManageStudyFields,
    OrganisationIndicationFields,
} from 'queries/generated/fragments.generated';
import { GET_MANAGE_ORG_STUDIES, GET_STUDY_CONFIG_EXPORT, IMPORT_STUDY_CONFIGURATION } from 'queries/studies';
import { GET_ORGANISATION } from 'queries/uam';
import { getLabels, getIsActiveField } from 'utils/convertor';
import { restructuredUpdatedCreatedBy } from '@utils-clean/update-users-array';
import NotificationService from 'utils/notification';
import AddStudyDialog from 'views/components/admin/add-study-dialog';
import AssignModulesDialog from 'views/components/admin/assign-modules';
import ExportStudyConfig from 'views/components/admin/export-study-config';
import ImportStudy from 'views/components/admin/import-study';
import ImportStudyError from 'views/components/admin/import-study-error';
import ImportSummary from 'views/components/admin/import-summary';
import SelectImportFile from 'views/components/admin/select-import-file';
import Button from 'views/components/core/buttons/button';
import ExpandableGrid from 'views/components/core/expandable/grid';
import { GridColumnMenuActiveFilter } from 'views/components/core/grid-filters';
import Loading from 'views/components/core/loading';
import PageTitle from 'views/components/core/page-title/page-title';
import Error from 'views/components/error/error';
import Constants from '@utils/constants';
import { MODAL_CLOSE_ACTIONS } from '@utils/modal-helper';

const AdminOrganisationStudies = () => {
    const { t } = useTranslation();
    const showAddStudyDialog = useBoolean(false);
    const showAssignModuleDialog = useBoolean(false);
    const showExportStudy = useBoolean(false);
    const showImportStudy = useBoolean(false);
    const showImportStudyError = useBoolean(false);
    const showImportStudyConfig = useBoolean(false);
    const showImportSummary = useBoolean(false);
    const [importResults, setImportResults] = useState(null);
    const [currentStudy, setCurrentStudy] = useState<ManageStudyFields>(null);
    const [availableStudies, setAvailableStudies] = useState<any[]>([]);
    const [importedConfig, setImportedConfig] = useState(null);
    const user = useAuthorization();
    const [loadPanel, { data, error, loading }] = useLazyQuery(GET_MANAGE_ORG_STUDIES);
    const [exportStudyConfiguration, { data: studyConfiguration, error: exportError, loading: exportLoading }] =
        useLazyQuery(GET_STUDY_CONFIG_EXPORT);
    const [importStudyConfiguration, { loading: importLoading }] = useMutation(IMPORT_STUDY_CONFIGURATION);
    const { MaxLengthCell, YesNoCell, DateCell, CustomizableCell } = useGridCells();
    const getOrganisationRequest = useQuery(GET_ORGANISATION, { variables: { id: user.organisationId } });
    const canEdit = user?.canWriteSystemAdminLevel();
    const isSysAdmin = user.isSystemAdmin(Constants.SystemAdminLevel.SysAdmin);

    useEffect(() => {
        loadPanel();
    }, [user.organisationId]);

    // Edit Study should use the SAVE_STUDY mutation
    /* This mutation takes a StudyInput variable that must fill out the following fields
       (all of these fields should come from the GET_MANAGE_ORG_STUDIES query):
            Field<ObjectIdType>("id");
            Field<StringGraphType>("studyNumber");
            Field<StringGraphType>("shortName");
            Field<StringGraphType>("title");
            Field<StringGraphType>("sponsor");
            Field<ObjectIdType>("evaluationModelId");
            Field<IntGraphType>("riskReviewWindowDays");
            Field<SubjectScheduleOptionTypeEnumType>("subjectScheduleOption");
            Field<ListGraphType<ObjectIdType>>("programs");
            Field<ListGraphType<ObjectIdType>>("products");
            Field<ListGraphType<ObjectIdType>>("indications");
            Field<StringGraphType>("code");
            Field<StringGraphType>("logo");
            Field<ListGraphType<StringGraphType>>("modules");
            Field<ListGraphType<ObjectIdType>>("riskLibraryCategoryIds");
            Field<ListGraphType<ObjectIdType>>("studyFunctionIds");
            Field<StringGraphType>("description");
            Field<DateTimeGraphType>("riskAssessmentRfpStartDate");
            Field<DateTimeGraphType>("riskAssessmentIsmpStartDate");
            Field<DateTimeGraphType>("centralMonitoringFirstDataLoadDate");
            Field<DateTimeGraphType>("centralMonitoringReleaseDate");
            Field<DateTimeGraphType>("riskAssessmentAwardStartDate");
     */
    // Assign Module(s) should use the UPDATE_STUDY_MODULES mutation
    const programs = getOrganisationRequest.data?.organisation.programs;
    const indications = getOrganisationRequest.data?.organisation.indications;
    const products = getOrganisationRequest.data?.organisation.products;
    const modules = getOrganisationRequest.data?.organisation.modules;

    const openAddStudyDialog = () => {
        showAddStudyDialog.setTrue();
    };

    const actions = (item: ManageStudyFields) => [
        {
            label: 'uam:editStudy',
            icon: ['fal', 'edit'],
            onClick: () => {
                setCurrentStudy(item);
                showAddStudyDialog.setTrue();
            },
            enabled: canEdit || isSysAdmin,
        },
        {
            label: 'admin:assignModules',
            icon: ['fal', 'link'],
            onClick: () => {
                setCurrentStudy(item);
                showAssignModuleDialog.setTrue();
            },
            enabled: canEdit,
        },
        {
            label: 'admin:exportStudyConfig',
            icon: ['fal', 'download'],
            onClick: () => {
                setCurrentStudy(item);
                showExportStudy.setTrue();
            },
            enabled: canEdit,
        },
        {
            label: 'admin:importStudyConfig',
            icon: ['fal', 'upload'],
            onClick: () => {
                setCurrentStudy(item);
                showImportStudyConfig.setTrue();
            },
            enabled: canEdit,
        },
    ];

    const download = (filename: string, text: string) => {
        const element = document.createElement('a');
        element.setAttribute('href', `data:text/plain;charset=utf-8,${encodeURIComponent(text)}`);
        element.setAttribute('download', filename);

        element.style.display = 'none';
        document.body.appendChild(element);
        element.click();
        document.body.removeChild(element);
    };

    useEffect(() => {
        if (exportError) NotificationService.showNotification(t('errorMessage'));
    }, [exportError]);

    useEffect(() => {
        if (studyConfiguration?.studyConfigExport) {
            const studyNumber =
                JSON.parse(studyConfiguration.studyConfigExport)?.StudyConfig?.Study?.StudyNumber ?? 'study';

            // Download it
            const today = new Date();
            // From: https://stackoverflow.com/questions/23593052/format-javascript-date-as-yyyy-mm-dd
            const formattedDate = new Date(today.getTime() - today.getTimezoneOffset() * 60000)
                .toISOString()
                .split('T')[0];
            download(`${studyNumber}_${formattedDate}.opra`, studyConfiguration.studyConfigExport);
            NotificationService.showNotification(t('admin:studyConfigSuccessfullyExported'));
        }
    }, [studyConfiguration]);

    const getAvailableProducts = (study: ManageStudyFields): string => {
        // Items can be duplicated
        const availableStudyProducts = products?.filter((product: OrganisationProductFields) =>
            study.productsIds.includes(product.id),
        );
        const availablePrograms = programs?.filter((program: OrganisationProgramFields) =>
            study.programsIds.includes(program.id),
        );
        const availableProgramProductsIds =
            availablePrograms?.reduce(
                (result: string[], program: OrganisationProgramFields) => result.concat(program.productIds),
                [],
            ) || [];
        const availableProgramProducts = products?.filter((product: OrganisationProductFields) =>
            availableProgramProductsIds.includes(product.id),
        );
        return (
            availableStudyProducts
                ?.concat(availableProgramProducts)
                .map((item: OrganisationProgramFields) => item.name)
                .join(', ') || ''
        );
    };

    const getAvailableIndications = (study: ManageStudyFields): string => {
        // Items can be duplicated
        const availableStudyIndications = indications?.filter((indication: OrganisationIndicationFields) =>
            study.indicationsIds.includes(indication.id),
        );
        const availablePrograms = programs?.filter((program: OrganisationProgramFields) =>
            study.programsIds.includes(program.id),
        );
        const availableProgramIndicationsIds =
            availablePrograms?.reduce(
                (result: string[], program: OrganisationProgramFields) => result.concat(program.indicationIds),
                [],
            ) || [];
        const availableProgramIndications = indications?.filter((ind: OrganisationIndicationFields) =>
            availableProgramIndicationsIds.includes(ind.id),
        );
        return (
            availableStudyIndications
                ?.concat(availableProgramIndications)
                .map((item: OrganisationIndicationFields) => item.name)
                .join(', ') || ''
        );
    };

    useEffect(() => {
        if (data?.organisationStudies) {
            const array = data?.organisationStudies.map((study: ManageStudyFields) => ({
                ...study,
                _isLogo: !!study.logo,
                _isRAM: getIsActiveField('RAM', study.modules),
                _isCM: getIsActiveField('CM', study.modules),
                _isCM2: getIsActiveField('CM2', study.modules),
                _isSM: getIsActiveField('SM', study.modules),
                _isDQ: getIsActiveField('DQ', study.modules),
                productsLabel: getAvailableProducts(study),
                indicationsLabel: getAvailableIndications(study),
                programsLabel: getLabels(study.programsIds, programs, 'name'),
                shortName: study.shortName || '',
                title: study.title || '',
            }));
            const restructuredArray = restructuredUpdatedCreatedBy(array);
            setAvailableStudies(orderBy(restructuredArray, [{ field: 'studyNumber', dir: 'asc' }]));
        }
    }, [data, getOrganisationRequest]);

    const getSummaryLine = (text: string, count: number) =>
        count ? `${count} ${text}${count > 1 ? 's' : ''} imported` : `No ${text}s imported`;

    const generateSummary = (results: any) => {
        if (!results) return '';

        return [
            results.study ? t('common:StudyConfigurationImported') : t('common:NoStudyConfigurationImported'),
            getSummaryLine(t('common:CRF'), results.studyCrfs),
            getSummaryLine(t('common:Assessment'), results.studyAssessments),
            getSummaryLine(t('common:Cohort'), results.studyCohorts),
            getSummaryLine(t('common:treatmentArm'), results.studyTreatmentArms),
            getSummaryLine(t('common:Panel'), results.panels),
            getSummaryLine(t('common:dataTable'), results.dataTables),
            getSummaryLine(t('common:Dashboard'), results.dashboards),
            getSummaryLine(t('common:extraDataField'), results.extraDataFields),
            getSummaryLine(t('common:subjectVisitSchedule'), results.svts),
            getSummaryLine(t('common:subjectVisit'), results.svtVisits),
            '\n',
            ...results.warnings.map((warning: string) => `Warning: ${warning}`),
            ...results.errors.map((e: string) => `Error: ${e}`),
        ]
            .filter((line: string) => line !== null)
            .join('\n');
    };

    if (!user || !user.systemAdminLevel) return null;

    if (loading || exportLoading) return <Loading />;

    if (error) return <Error message={error} />;

    return (
        <>
            <ImportSummary
                open={showImportSummary.value}
                summary={generateSummary(importResults)}
                onClose={() => {
                    showImportSummary.setFalse();
                }}
            />
            <ImportStudyError
                open={showImportStudyError.value}
                onClose={() => {
                    showImportStudyError.setFalse();
                }}
            />
            <ImportStudy
                open={false}
                isImporting={importLoading}
                onClose={(selectedItems: any) => {
                    if (!selectedItems) {
                        // Close and do nothing
                        showImportStudyConfig.setFalse();
                        return;
                    }

                    // Do the import
                    importStudyConfiguration({
                        variables: {
                            contents: JSON.stringify(importedConfig),
                            studyId: currentStudy.id,
                            importTypes: selectedItems.map((type: any) => type.id),
                        },
                    }).then((result: any) => {
                        showImportStudyConfig.setFalse();
                        setImportResults(result?.data?.importStudyConfiguration);
                        showImportSummary.setTrue();
                        loadPanel();
                        setCurrentStudy(null);
                    });
                }}
            />
            {showImportStudyConfig.value && (
                <ImportStudyConfig
                    studyId={currentStudy.id}
                    isImporting={importLoading}
                    onClose={(action?: string) => {
                        if (action === MODAL_CLOSE_ACTIONS.ERROR)
                            NotificationService.showNotification(t('errorMessage'));
                        showImportStudyConfig.setFalse();
                    }}
                />
            )}
            <SelectImportFile
                open={showImportStudy.value}
                onClose={(config?: any) => {
                    showImportStudy.setFalse();
                    if (config) {
                        showImportStudyConfig.setTrue();
                        setImportedConfig(config);
                    }
                }}
            />
            <ExportStudyConfig
                open={showExportStudy.value}
                onClose={(result?: boolean) => {
                    setCurrentStudy(null);
                    showExportStudy.setFalse();

                    if (result) exportStudyConfiguration({ variables: { studyId: currentStudy.id } });
                }}
                study={currentStudy}
            />
            <AssignModulesDialog
                organisationId={user.organisationId}
                modules={modules}
                currentStudy={currentStudy}
                close={() => {
                    setCurrentStudy(null);
                    showAssignModuleDialog.setFalse();
                    NavigationService.setUpdateStudies(user.organisationKey);
                }}
                open={showAssignModuleDialog.value}
            />
            <AddStudyDialog
                organisationId={user.organisationId}
                organisationKey={user.organisationKey}
                currentStudy={currentStudy}
                programs={programs}
                modules={modules}
                open={showAddStudyDialog.value}
                studies={availableStudies}
                close={() => {
                    setCurrentStudy(null);
                    showAddStudyDialog.setFalse();
                }}
                isSysAdmin={isSysAdmin}
            />
            <PageTitle text={t('uam:studies')} />
            <div className="btn-wrapper-create-study top-title-btn-wrapper">
                {/* Removed temporarily - may go back in a future CC
                <Button
                    className="study-import-btn main-button"
                    text="admin:importStudyConfiguration"
                    onClick={openImportStudyDialog}
                    disabled={!canEdit}
                    style={{marginRight: 20}}
                />
                */}
                <Button
                    className="study-add-btn main-button"
                    text="admin:addStudy"
                    onClick={openAddStudyDialog}
                    disabled={!canEdit}
                />
            </div>
            <ExpandableGrid
                customKey="studies-grid"
                id="studiesGrid"
                key="studiesGrid"
                items={availableStudies}
                rowActions={actions}
                groupable={false}
                showPager
                scrollable="scrollable"
                reorderable
                addScroll
                pageable={false}
                resizable
                options={{
                    allowExcelExport: false,
                    allowExpandCollapseAll: false,
                    useFilterChips: false,
                }}
                detail={null}
                columns={[
                    {
                        field: 'studyNumber',
                        label: t('cm:study_num'),
                        id: 'study-studyNumber',
                        minWidth: 140,
                        lockedByDefault: true,
                        cells: { data: MaxLengthCell(20) },
                    },
                    {
                        field: 'shortName',
                        label: t('admin:shortName'),
                        id: 'study-shortName',
                        minWidth: 165,
                        cells: { data: MaxLengthCell(70) },
                    },
                    {
                        field: 'title',
                        label: t('admin:title'),
                        id: 'study-title',
                        minWidth: 160,
                        cells: { data: MaxLengthCell(150) },
                    },
                    {
                        field: '_isLogo',
                        label: t('admin:logoAdded'),
                        id: 'study-logoAdded',
                        minWidth: 165,
                        cells: { data: YesNoCell },
                        filterUi: GridColumnMenuActiveFilter,
                    },
                    {
                        field: 'useLatestCentralMonitoring',
                        label: t('admin:latestCM'),
                        id: 'study-useLatestCentralMonitoring',
                        minWidth: 165,
                        cells: { data: YesNoCell },
                        filterUi: GridColumnMenuActiveFilter,
                    },
                    {
                        field: '_isRAM',
                        label: t('admin:RAMShort'),
                        id: 'study-RAM',
                        minWidth: 150,
                        cells: { data: CustomizableCell((dataItem: any) => (dataItem._isRAM ? t('active') : '')) },
                        filterUi: GridColumnMenuActiveFilter,
                    },
                    {
                        field: '_isCM2',
                        label: t('admin:CMShort'),
                        id: 'study-CM2',
                        minWidth: 150,
                        cells: { data: CustomizableCell((dataItem: any) => (dataItem._isCM2 ? t('active') : '')) },
                        filterUi: GridColumnMenuActiveFilter,
                    },
                    {
                        field: '_isCM',
                        label: t('admin:CMLegacyShort'),
                        id: 'study-CM',
                        minWidth: 185,
                        cells: { data: CustomizableCell((dataItem: any) => (dataItem._isCM ? t('active') : '')) },
                        filterUi: GridColumnMenuActiveFilter,
                    },
                    {
                        field: '_isSM',
                        label: t('admin:SMCore'),
                        id: 'study-SM',
                        minWidth: 185,
                        cells: { data: CustomizableCell((dataItem: any) => (dataItem._isSM ? t('active') : '')) },
                        filterUi: GridColumnMenuActiveFilter,
                    },
                    {
                        field: '_isDQ',
                        label: t('admin:DQ'),
                        id: 'study-DQ',
                        minWidth: 185,
                        cells: { data: CustomizableCell((dataItem: any) => (dataItem._isDQ ? t('active') : '')) },
                        filterUi: GridColumnMenuActiveFilter,
                    },
                    {
                        field: 'programsLabel',
                        label: t('admin:program'),
                        id: 'study-programsLabel',
                        minWidth: 160,
                    },
                    {
                        field: 'indicationsLabel',
                        label: t('admin:indicationS'),
                        id: 'study-indicationsLabel',
                        minWidth: 160,
                    },
                    {
                        field: 'productsLabel',
                        label: t('admin:productS'),
                        id: 'study-productsLabel',
                        minWidth: 160,
                    },
                    {
                        label: t('ra:createdBy'),
                        field: '_createdByString',
                        id: 'study-createdby',
                        minWidth: 180,
                    },
                    {
                        label: t('ra:createdDate'),
                        field: 'created',
                        id: 'study-created',
                        filter: 'date',
                        minWidth: 180,
                        cells: { data: DateCell },
                    },
                    {
                        label: t('ra:updatedBy'),
                        field: '_updatedByString',
                        id: 'study-updatedby',
                        minWidth: 180,
                    },
                    {
                        label: t('ra:updatedDate'),
                        field: 'updated',
                        id: 'study-updated',
                        filter: 'date',
                        minWidth: 190,
                        cells: { data: DateCell },
                    },
                    {
                        field: 'sponsor',
                        label: t('admin:sponsor'),
                        id: 'study-sponsor',
                        minWidth: 140,
                        showByDefault: false,
                    },
                    {
                        field: 'recordId',
                        label: t('id'),
                        id: 'study-id',
                        filter: 'numeric',
                        minWidth: 140,
                        showByDefault: false,
                    },
                ]}
            />
        </>
    );
};

export default AdminOrganisationStudies;
